import {Injectable} from '@angular/core';
import {combineLatest, forkJoin, merge, Observable, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {OrderSearchParams} from 'src/models/orders/orderSearchParams.model';
import {SelectItem} from 'src/models/general/select.item.model';
import {OrderListResponse} from 'src/models/orders/orderListResponse';
import {OrderDetailModel} from 'src/models/orders/orderDetails/orderDetail.model';
import {ConfirmOrderArticle} from 'src/models/orders/orderConfirm/confirmOrderArticle.model';
import {OrderService} from 'src/services/orders/order.service';
import {AuthenticationService} from 'src/services/authentication/authentication.service';
import {
	CancelOrderUpdate,
	ExportConfirmData,
	OrderUpdate,
	PaymentMethodUpdate,
	RemarksUpdate,
	ReviewStateUpdate
} from 'src/models/orders/order.service.models';
import {OrderListModel} from 'src/models/orders/orderList.model';
import {OrderFilterService, OrderFilterValues} from 'src/services/orders/order.filter.service';
import {OrderPaginationService} from 'src/services/orders/order.pagination.service';
import {Pagination} from 'src/models/general/pagination.model';
import {OrderSortService} from 'src/services/orders/order.sort.service';
import {Params} from '@angular/router';
import {VatValidationResult} from 'src/models/orders/vat/vat.validation.result.model';
import {Shipment, ShipmentPosition, ShipmentServiceProvider} from 'src/models/orders/orderDetails/shipment.model';
import {OrderShipmentService} from 'src/services/orders/order.shipment.service';
import {TrackingLinkViewModel} from 'src/models/orders/tracking/tracking.link.view.model';
import {OrderInvoiceService} from 'src/services/orders/order.invoice.service';
import {OrderInvoice} from 'src/models/orders/invoice/order.invoice.model';
import {OrderFormService} from 'src/services/orders/order-form.service';
import {UntypedFormGroup} from '@angular/forms';
import {OrderItemModel} from 'src/models/orders/orderItems/orderItems.model';
import {SalesChannel} from 'src/models/orders/sales.channel.enum';
import {SalesChannelWithWorkflowStatus} from 'src/models/orders/salesChannelWithWorkflowStatus';
import {SalesChannelWithAvailableActions} from 'src/models/orders/salesChannelWithAvailableActions';
import {AvailableAction} from 'src/models/orders/available.action.dto';
import {AvailableActionHandler} from 'src/modules/sales/utils/availableActionHandler';
import {OrderAction} from 'src/models/orders/orderActions';
import {SortValue} from 'src/models/general/sort.model';

@Injectable({
	providedIn: 'root'
})
export class OrderFacade {
	constructor(private orderApiService: OrderService,
				private orderFilterService: OrderFilterService,
				private orderSortService: OrderSortService,
				private orderFormService: OrderFormService,
				private orderPaginationService: OrderPaginationService,
				private orderShipmentService: OrderShipmentService,
				private orderInvoiceService: OrderInvoiceService,
				private authenticationService: AuthenticationService) {
	}

	getOrders(): Observable<OrderListResponse | null> {
		// Wait for changes on any of these 3
		return combineLatest([
			this.orderPaginationService.getPaginationObservable(),
			this.orderFilterService.getOrderFilterObservable(),
			this.orderSortService.getSortObservable()
		]).pipe(
			distinctUntilChanged(),
			debounceTime(100),
			// On every change create a new params object
			map((data: [Pagination, OrderFilterValues, SortValue]) => {
				const params = new OrderSearchParams();
				params.initPagination(data[0]);
				params.initOrderFilter(data[1]);
				params.initSort(data[2]);
				return params;
			}),
			switchMap((params: OrderSearchParams) => {
				return this.orderApiService.getAlwaysAllowedActions().pipe(
					map((salesChannelActions: SalesChannelWithAvailableActions) => {
						this.handlePaymentFilterForSalesChannel(params, salesChannelActions);
						return params;
					})
				);
			}),
			// Return a new observable for the component/async-pipe
			switchMap((searchParams: OrderSearchParams) => {
				return merge(
					// First, emit "null" to trigger the loader
					of(null),
					// Directly followed by the actual call
					this.orderApiService.getOrders(searchParams)
				);
			})
		);
	}

	private handlePaymentFilterForSalesChannel(filterValues: OrderSearchParams, salesChannelActions: SalesChannelWithAvailableActions): void {
		const channel = filterValues.salesChannel as SalesChannel;

		const actionHandler = new AvailableActionHandler(salesChannelActions[channel], OrderAction.ShowPaymentFilter);
		// reset paymentFilter for all sales-channels which don't have a payment-filter
		// we do this on the searchparams to keep the selected payment-methods for the user
		// we only send an empty list to the BE - FE "thinks" they are still selected
		if (!actionHandler.actionAllowed()) {
			filterValues.paymentMethodFilter = [];
		}
	}

	getOrderById(orderId: string): Observable<OrderDetailModel> {
		return this.orderApiService.getOrderById(orderId);
	}

	confirmOrder(confirmations: ConfirmOrderArticle[], id: string): Observable<any> {
		return this.orderApiService.confirmOrder(confirmations, id);
	}

	confirmOrderExport(order: OrderDetailModel | OrderListModel): Observable<void> {
		return this.authenticationService.getUsername().pipe(
			map((username: string | undefined) => {
				return {
					orderUuid: order.id,
					assignedUser: username || ''
				};
			}),
			switchMap((exportConfirmData: ExportConfirmData) => {
				return this.orderApiService.confirmOrderExport(exportConfirmData);
			}),
		);
	}

	validateVatId(order: OrderDetailModel): Observable<any> {
		return this.orderApiService.validateVatId(order);
	}

	getRejectionReasonsAsSelectItem(): Observable<SelectItem[]> {
		return this.orderApiService.getRejectionReasonsAsSelectItem();
	}

	updateCustomerNumber(value: OrderUpdate, orderId: string): Observable<void> {
		return this.orderApiService.updateCustomerNumber(value, orderId);
	}

	updateReviewState(orderUuid: string, newReviewState: boolean): Observable<void> {
		return this.authenticationService.getUsername().pipe(
			map((username: string | undefined) => {
				return {
					reviewState: newReviewState,
					assignedUser: username || ''
				};
			}),
			switchMap((reviewStateData: ReviewStateUpdate) => {
				return this.orderApiService.updateReviewState(orderUuid, reviewStateData);
			}),
		);
	}

	cancelOrder(orderUuid: string, orderCancelRemarks: CancelOrderUpdate): Observable<void> {
		return this.authenticationService.getUsername().pipe(
			map((username: string | undefined) => {
				orderCancelRemarks.assignedUser = username || '';
				return orderCancelRemarks;
			}),
			switchMap((cancelOrderUpdate: CancelOrderUpdate) => {
				return this.orderApiService.cancelOrder(orderUuid, cancelOrderUpdate);
			}),
		);
	}

	updateComment(orderUuid: string, remarks: RemarksUpdate): Observable<void> {
		return this.authenticationService.getUsername().pipe(
			map((username: string | undefined) => {
				remarks.assignedUser = username || '';
				return remarks;
			}),
			switchMap(() => {
				return this.orderApiService.updateComment(orderUuid, remarks);
			})
		);
	}

	updateOrder(order: OrderDetailModel): Observable<OrderDetailModel> {
		return this.orderApiService.updateOrder(order);
	}

	setOrderPaid(orderId: string): any {
		return this.orderApiService.setOrderPaid(orderId);
	}

	updatePagination(pagination: Pagination): void {
		this.orderPaginationService.updatePagination(pagination);
	}

	updateSort(sort: SortValue): void {
		this.orderSortService.updateSort(sort);
	}

	getCurrentListSortValue(): SortValue {
		return this.orderSortService.getCurrentSortValue();
	}

	createFilterFromRouterParams(params: Params): OrderFilterValues {
		return this.orderFilterService.updateFilterFromRouteParams(params);
	}

	getSearchFilterValue(): OrderFilterValues {
		return this.orderFilterService.getCurrentSearchFilterValues();
	}

	setNewFilter(newFilter: OrderFilterValues): void {
		this.orderFilterService.setNewFilter(newFilter);
		this.orderPaginationService.goToFirstPage();
	}

	setNewSearchQuery(query: string, orderNumberQuery: string): void {
		this.orderFilterService.setNewSearchQuery(query, orderNumberQuery);
		this.orderPaginationService.goToFirstPage();
	}

	getLastVatIdLog(orderUuid: string): Observable<VatValidationResult | undefined> {
		return this.orderApiService.getVatIdLogs(orderUuid, true).pipe(
			map((data: VatValidationResult[]) => {
				if (!data || data.length === 0) {
					return undefined;
				}

				const result = data[0];

				// Indicates a missing Vat-ID - a bit silly from BE :shrug:
				if (!result.validationMessage) {
					return undefined;
				}

				return result;
			})
		);
	}

	getShipmentsForOrder(order: OrderDetailModel): Observable<Shipment[]> {
		return this.orderShipmentService.getShipments(order.id).pipe(
			switchMap((shipments: Shipment[]) => {
				if (shipments.length > 0) {
					return of(shipments);
				}
				return this.createFakeShipment(order);
			})
		);
	}

	getShipmentProvidersAsSelectItem(): Observable<SelectItem[]> {
		return this.orderShipmentService.getShipmentProvidersAsSelectItem();
	}

	getTrackingLinksForOrder(order: OrderDetailModel): Observable<TrackingLinkViewModel[]> {
		return forkJoin([
			this.orderShipmentService.getShipmentProviders(),
			this.getShipmentsForOrder(order),
		]).pipe(
			map((data: [ShipmentServiceProvider[], Shipment[]]) => {
				return this.orderShipmentService.mapShipmentsToTrackingLinkViewModels(data);
			})
		);
	}

	updateTrackingNumbers(param: Shipment[]): Observable<void[]> {
		return forkJoin(
			param.map((entry: Shipment) => {
				return this.orderShipmentService.updateShipments(entry);
			})
		);
	}

	getInvoicesFor(orderId: string): Observable<OrderInvoice[]> {
		return this.orderInvoiceService.getInvoicesFor(orderId);
	}

	createOrderEditForm(order: OrderDetailModel): UntypedFormGroup {
		return this.orderFormService.createOrderEditForm(order);
	}

	createOrderItemEditForm(order: OrderDetailModel): UntypedFormGroup {
		return this.orderFormService.createOrderItemEditForm(order);
	}

	updateOrderItems(orderUuid: string, orderItems: OrderItemModel[]): Observable<void> {
		return this.orderApiService.updateOrderItems(orderUuid, orderItems);
	}

	updatePaymentMethod(orderUuid: string, value: PaymentMethodUpdate): Observable<void> {
		return this.orderApiService.updatePaymentMethod(orderUuid, value);
	}

	private createFakeShipment(orderDetails: OrderDetailModel): Observable<Shipment[]> {
		const shipmentPositions = this.createShipmentPositionsFrom(orderDetails);
		const fakeShipment = [{
			trackingNumbers: [],
			shipmentPositions,
			orderUuid: orderDetails.id,
			shipmentDate: new Date().toISOString(),
			shipmentNumber: 'Manuell',
			manual: true,
		} as Shipment];

		return of(fakeShipment);
	}

	private createShipmentPositionsFrom(orderDetails: OrderDetailModel): ShipmentPosition[] {
		return orderDetails.orderItems.map((item: OrderItemModel) => {
			// TODO: Das sollte für marktplätze ohne "confirm" immer auf ordered stehen
			const quantity = orderDetails.salesChannel === SalesChannel.Egis ? item.quantityConfirmed : item.orderedAmount;
			return {
				shippingQuantity: quantity,
				productNumber: item.sku
			} as ShipmentPosition;
		});
	}

	getWorkflowStatusWithSalesChannels(): Observable<SalesChannelWithWorkflowStatus> {
		return this.orderApiService.getWorkflowStatesFor();
	}


	setPaymentFilters(activePaymentMethods: string[]): void {
		this.orderFilterService.setPaymentFilters(activePaymentMethods);
	}

	getActionsForCurrentSalesChannel(): Observable<AvailableAction[]> {
		return this.orderFilterService.getOrderFilterObservable().pipe(
			switchMap((values: OrderFilterValues) => {
				return this.orderApiService.getAlwaysAllowedActions().pipe(
					map((channelsWithActions: SalesChannelWithAvailableActions) => {
						return channelsWithActions[values.salesChannel];
					})
				);
			})
		);
	}
}
